import { Search } from "@mui/icons-material";
import {
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Popover,
  Popper,
  Select,
  TextField,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { CiFilter } from "react-icons/ci";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import DatePicker from "react-datepicker";

import useDebounce from "../../hooks/useDebounce";
import {
  getBillingListNew,
  getBillingListPbf,
} from "../../../services/billingAPI";
import PagePagination from "../../common/particles.jsx/PagePagination";
import ModalPaymentStatus from "./ModalPaymentStatus";
import { STATUS_ONLINE_BILLING } from "../../constants/constant";
import { getPaymentMethodNew } from "../../../services/purchaseApi";

const toIDR = (val) =>
  val?.toLocaleString("id-ID", {
    style: "currency",
    currency: "IDR",
  });

const INITIAL_REQ = {
  offset: 1,
  limit: 50,
  status: [1, 2, 3, 4, 5, 6],
  start_date: "",
  end_date: "",
  period: 0,
  sort_date: 1, //1. newest, 2. longest
  sort_due_date: 1, //1.newest, 2. longest
  is_due_date: true,
};

const OnlineBilling = ({ isPbf }) => {
  const navigate = useNavigate();
  const { pageSetting } = useParams();

  const [reqBody, setReqBody] = useState(INITIAL_REQ);
  const [search, setSearch] = useState("");
  const [anchorElStatus, setAnchorElStatus] = useState(null);
  const [openCalendar, setOpenCalendar] = useState(false);
  const [billingIdArr, setBillingIdArr] = useState([]);
  const [periodDummy, setPeriodDummy] = useState(0);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [moreProductArr, setMoreProductArr] = useState([]);
  const [anchorElMoreProduct, setAnchorElMoreProduct] = useState(null);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [selectedBilling, setSelectedBilling] = useState(null);
  const [openModalPay, setOpenModalPay] = useState(false);
  const [isMultiple, setIsMultiple] = useState(false);

  const debounce = useDebounce(search, 500);

  const getList = (body) => {
    // if (isPbf) mutateListPbf(body);
    // else
    mutateList(body);
    setBillingIdArr([]);
  };

  const isAllPaid = () => {
    var temp = [];
    billingIdArr.map((id) => {
      temp.push(
        dataList.data.find((item) =>
          isPbf
            ? item.billing_uid === id.billing_uid
            : item.billing_id === id.billing_id,
        ),
      );
    });

    return temp.every(
      (item) => item && (item.status === 4 || item.status === 6),
    );
  };

  const isAllNotPaid = () => {
    var temp = [];
    billingIdArr.map((id) => {
      temp.push(
        dataList.data.find((item) =>
          isPbf
            ? item.billing_uid === id.billing_uid
            : item.billing_id === id.billing_id,
        ),
      );
    });

    return temp.every(
      (item) => item && (item.status === 1 || item.status === 3),
    );
  };

  const handleSelectAll = () => {
    const isEmpty = billingIdArr.length === 0;
    setBillingIdArr(
      isEmpty ? dataList.data.filter((item) => item.status !== 4) : [],
    );
  };

  const getTotalBilling = () => {
    var total = 0;
    billingIdArr.map((id) => {
      const bill = dataList.data.find((item) =>
        isPbf
          ? item.billing_uid === id.billing_uid
          : item.billing_id === id.billing_id,
      );
      if (bill && bill.status !== 4) total = total + bill.total_bill;
    });

    return total;
  };

  const handleAddBillingId = (bill) => {
    const index = billingIdArr.findIndex((item) =>
      isPbf
        ? item.billing_uid === bill.billing_uid
        : item.billing_id === bill.billing_id,
    );
    if (index < 0) setBillingIdArr([...billingIdArr, bill]);
    else
      setBillingIdArr([
        ...billingIdArr.slice(0, index),
        ...billingIdArr.slice(index + 1),
      ]);
  };

  const navigateTo = (
    newOffset,
    newLimit,
    newStart,
    newEnd,
    newStatus,
    newPeriod,
    newSortDate,
    newSortDue,
    isDue,
  ) => {
    navigate(
      `/online-billing${isPbf ? "-pbf" : ""}/offset=${newOffset}&limit=${newLimit}&period=${newPeriod}&start_date=${newStart}&end_date=${newEnd}&status=${encodeURIComponent(
        newStatus,
      )}&sort_date=${newSortDate}&sort_due_date=${newSortDue}&is_due_date=${isDue}`,
    );
  };

  const {
    mutate: mutateList,
    data: dataList,
    isLoading,
  } = useMutation({
    mutationFn: isPbf ? getBillingListPbf : getBillingListNew,
  });

  // const {
  //   mutate: mutateListPbf,
  //   data: dataListPbf,
  //   isLoading: isLoadingPbf,
  // } = useMutation({
  //   mutationFn: ,
  // });

  const { data: dataPaymentMethod, isFetching: isFetchingPaymentMethod } =
    useQuery({
      queryKey: ["payment-method"],
      queryFn: () => getPaymentMethodNew(),
      refetchOnWindowFocus: false,
    });

  const creditLimitData = useMemo(() => {
    return dataPaymentMethod &&
      !isFetchingPaymentMethod &&
      dataPaymentMethod.card.find((item) => item.id === 23)
      ? dataPaymentMethod.card.find((item) => item.id === 23)
      : null;
  }, [dataPaymentMethod]);

  useEffect(() => {
    getList({ ...reqBody, search: debounce });
  }, [reqBody, debounce]);

  useEffect(() => {
    setReqBody((prevVal) => ({
      ...prevVal,
      offset: 1,
    }));
  }, [debounce]);

  useEffect(() => {
    setIsSelectAll(
      dataList && dataList.data && !isLoading && billingIdArr.length > 0
        ? dataList.data.filter((item) => item.status < 4).length ===
            billingIdArr.length
        : false,
    );
  }, [billingIdArr]);

  useEffect(() => {
    if (pageSetting) {
      var limitSetting,
        offsetSetting,
        period,
        startDate,
        endDate,
        statusSetting,
        sortDate,
        sortDueDate,
        isDue;

      pageSetting.split("&").map((item) => {
        var val = item.split("=");
        if (val[0] === "limit") limitSetting = val[1];
        if (val[0] === "offset") offsetSetting = val[1];
        if (val[0] === "period") period = val[1];
        if (val[0] === "start_date") startDate = val[1];
        if (val[0] === "end_date") endDate = val[1];
        if (val[0] === "status")
          statusSetting = decodeURIComponent(val[1])
            .split(",")
            .map((item) => {
              return Number(item);
            });

        if (val[0] === "sort_date") sortDate = val[1];
        if (val[0] === "sort_due_date") sortDueDate = val[1];
        if (val[0] === "is_due_date") isDue = val[1];
      });

      setReqBody((prevVal) => ({
        ...prevVal,
        limit: limitSetting ? Number(limitSetting) : 50,
        offset: offsetSetting ? Number(offsetSetting) : 1,
        period: period ? Number(period) : 0,
        start_date: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
        end_date: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
        status:
          statusSetting && statusSetting !== ""
            ? statusSetting.length > 0 &&
              !(statusSetting.length === 1 && statusSetting[0] === 0)
              ? statusSetting
              : []
            : [],
        sort_date: Number(sortDate),
        sort_due_date: Number(sortDueDate),
        is_due_date: isDue && isDue === "true" ? true : false,
      }));
      setPeriodDummy(period ? (Number(period) === 9 ? 10 : Number(period)) : 0);
    }
  }, [pageSetting]);

  return (
    <>
      <div className="text-[12px] px-3 pt-3 pb-[70px]">
        <div className="block lg:flex items-stretch gap-2 mb-2 overflow-x-auto h-full">
          <div className="w-full min-h-full  self-stretch flex flex-col items-stretch justify-between">
            <TextField
              sx={{
                marginBottom: 1,
                "& .MuiOutlinedInput-root": {
                  padding: 1,
                },
              }}
              placeholder="Cari data"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment>
                    <Search />
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
            <div className="flex items-center gap-2 items-stretch self-stretch h-full">
              <div
                className="self-stretch border border-gray-400 rounded-md flex items-center justify-between gap-4 w-full h-full py-2.5 px-2 text-gray-500 cursor-pointer hover:bg-gray-100"
                onClick={(e) => setAnchorElStatus(e.currentTarget)}
              >
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <CiFilter className="text-[18px]" />{" "}
                  <p className="w-full">
                    {reqBody.status.length === STATUS_ONLINE_BILLING.length
                      ? "All Status"
                      : reqBody.status.length < STATUS_ONLINE_BILLING.length &&
                          reqBody.status.length > 1
                        ? "Multiple Status"
                        : reqBody.status.length === 1
                          ? STATUS_ONLINE_BILLING.find(
                              (item) => item.id === reqBody.status[0],
                            )?.value
                          : "Status"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {reqBody.status.length < STATUS_ONLINE_BILLING.length &&
                  reqBody.status.length > 1 ? (
                    <div className="p-[4px] w-[18px] h-[18px] flex justify-center items-center rounded-full bg-gray-200">
                      {reqBody.status.length}
                    </div>
                  ) : null}
                  <FaChevronDown />
                </div>
              </div>
              <Select
                value={periodDummy}
                sx={{ width: "100%" }}
                onChange={(e) => {
                  setReqBody((prevValue) => ({
                    ...prevValue,
                    period: e.target.value === 10 ? 9 : e.target.value,
                    start_date: "",
                    end_date: "",
                  }));
                  if (e.target.value === 9) {
                    setOpenCalendar(true);
                    return;
                  } else if (e.target.value !== 10) {
                    navigateTo(
                      reqBody.offset,
                      reqBody.limit,
                      "",
                      "",
                      reqBody.status,
                      e.target.value,
                      reqBody.sort_date,
                      reqBody.sort_due_date,
                      reqBody.is_due_date,
                    );
                    setOpenCalendar(false);
                    setStartDate(null);
                    setEndDate(null);
                  }
                  setPeriodDummy(e.target.value);
                }}
              >
                <MenuItem value={0}>Semua Periode</MenuItem>
                <MenuItem value={1}>Hari ini</MenuItem>
                <MenuItem value={2}>Kemarin</MenuItem>
                <MenuItem value={4}>Minggu Berjalan</MenuItem>
                <MenuItem value={3}>Minggu Lalu</MenuItem>
                <MenuItem value={6}>Bulan Berjalan</MenuItem>
                <MenuItem value={5}>Bulan Lalu</MenuItem>
                <MenuItem value={8}>Tahun Berjalan</MenuItem>
                <MenuItem value={7}>Tahun Lalu</MenuItem>
                <MenuItem value={9}>Pilih Tanggal</MenuItem>
                <MenuItem value={10} disabled sx={{ display: "none" }}>
                  {moment(reqBody.start_date).format("DD/MM/YYYY") +
                    " - " +
                    moment(reqBody.end_date).format("DD/MM/YYYY")}
                </MenuItem>
              </Select>
            </div>
          </div>
          <div className="mt-2 lg:!mt-0 min-h-full self-stretch border-2 rounded-md p-3 border-gray-400 overflow-x-auto w-full">
            <div className="flex items-center gap-2">
              <p className="font-semibold">OLIN Credit Limit</p>
              <div>
                {creditLimitData ? (
                  <div
                    className={`py-1 px-3 rounded-full w-fit flex items-center gap-2 ${
                      creditLimitData && creditLimitData.status === 0
                        ? "bg-red-100 text-red-500"
                        : "bg-blue-100 text-blue-500"
                    }`}
                  >
                    {creditLimitData.status === 0
                      ? "Ditolak"
                      : creditLimitData.status === 1
                        ? "Aktif"
                        : creditLimitData.status === 2
                          ? "Aktifkan"
                          : ""}
                  </div>
                ) : null}
              </div>
            </div>
            {creditLimitData ? (
              <>
                <div className="flex justify-between items-center">
                  <p>Tersedia</p>
                  <p>{toIDR(creditLimitData.limit)}</p>
                </div>
                <div className="flex justify-between items-center">
                  <p>Jumlah Pemakaian</p>
                  <p>
                    {toIDR(
                      creditLimitData.actual_limit - creditLimitData.limit,
                    )}
                  </p>
                </div>
                <div className="flex justify-between items-center">
                  <p>Total Kredit</p>
                  <p>{toIDR(creditLimitData.actual_limit)}</p>
                </div>
              </>
            ) : isFetchingPaymentMethod ? (
              <CircularProgress size={20} />
            ) : (
              <p>Gagal menarik data credit limit </p>
            )}
          </div>
        </div>

        <div className="flex w-full mb-8">
          <div className="w-full">
            <div className="block overflow-x-auto w-full rounded-md">
              <table className="w-full overflow-x-auto rounded-md border-collapse">
                <thead className="bg-blue-500 text-white">
                  <th className="p-2 text-center">
                    <Checkbox
                      disabled={
                        isLoading ||
                        !dataList ||
                        (dataList && dataList.count === 0)
                      }
                      onChange={handleSelectAll}
                      checked={isSelectAll}
                    />
                  </th>
                  <th className="p-2 text-center">No</th>
                  <th
                    className="p-2 text-center whitespace-nowrap cursor-pointer"
                    onClick={() => {
                      navigateTo(
                        reqBody.offset,
                        reqBody.limit,
                        reqBody.start_date,
                        reqBody.end_date,
                        reqBody.status,
                        reqBody.period,
                        reqBody.sort_date === 1 ? 2 : 1,
                        reqBody.sort_due_date,
                        false,
                      );
                      setReqBody((prevValue) => ({
                        ...prevValue,
                        sort_date: prevValue.sort_date === 1 ? 2 : 1,
                        offset: 1,
                        is_due_date: false,
                      }));
                    }}
                  >
                    <div className="w-full flex gap-2 items-center">
                      <p className="whitespace-nowrap text-nowrap">Tanggal</p>
                      {reqBody.sort_date === 1 ? (
                        <FaChevronUp className="text-[14px]" />
                      ) : reqBody.sort_date === 2 ? (
                        <FaChevronDown className="text-[14px]" />
                      ) : null}
                    </div>
                  </th>
                  <th className="p-2 text-center whitespace-nowrap">
                    No Invoice
                  </th>
                  {isPbf ? null : (
                    <th className="p-2 text-center whitespace-nowrap">
                      No MPI
                    </th>
                  )}
                  <th className="p-2 text-left whitespace-nowrap w-[300px]">
                    Produk
                  </th>
                  <th className="p-2 text-center whitespace-nowrap">
                    Total Transaksi
                  </th>
                  {isPbf ? null : (
                    <th className="p-2 text-center whitespace-nowrap">
                      Sumber
                    </th>
                  )}
                  <th className="p-2 text-center whitespace-nowrap">Status</th>
                  <th
                    className="p-2 text-center whitespace-nowrap cursor-pointer"
                    onClick={() => {
                      navigateTo(
                        reqBody.offset,
                        reqBody.limit,
                        reqBody.start_date,
                        reqBody.end_date,
                        reqBody.status,
                        reqBody.period,
                        reqBody.sort_date,
                        reqBody.sort_due_date === 1 ? 2 : 1,
                        true,
                      );
                      setReqBody((prevValue) => ({
                        ...prevValue,
                        sort_due_date: prevValue.sort_due_date === 1 ? 2 : 1,
                        offset: 1,
                        is_due_date: true,
                      }));
                    }}
                  >
                    <div className="w-full flex gap-2 items-center">
                      <p className="whitespace-nowrap text-nowrap">
                        Jatuh Tempo
                      </p>
                      {reqBody.sort_due_date === 1 ? (
                        <FaChevronUp className="text-[14px]" />
                      ) : reqBody.sort_due_date === 2 ? (
                        <FaChevronDown className="text-[14px]" />
                      ) : null}
                    </div>
                  </th>
                  <th className="p-2 text-center whitespace-nowrap">Action</th>
                </thead>
                <tbody>
                  {dataList && !isLoading && dataList.data ? (
                    //&&
                    // dataList.count > 0
                    dataList.data.map((item, i) => (
                      <tr className="border" key={i}>
                        <td className="p-2 text-center">
                          <Checkbox
                            disabled={item.status === 4}
                            checked={billingIdArr.some((bill) =>
                              isPbf
                                ? item.billing_uid === bill.billing_uid
                                : item.billing_id === bill.billing_id,
                            )}
                            onChange={() => handleAddBillingId(item)}
                          />
                        </td>
                        <td className="p-2 text-center">
                          {(reqBody.offset - 1) * reqBody.limit + (i + 1)}
                        </td>
                        <td className="p-2 text-center">
                          {moment(
                            isPbf ? item.date_transaction : item.created_at,
                          ).format("DD/MM/YYYY ")}
                        </td>
                        <td className="p-2 text-center text-nowrap">
                          {isPbf ? item.invoice_no : item.invoice_number}
                        </td>
                        {isPbf ? null : (
                          <td className="p-2 text-center">
                            {item.faktur_number}
                          </td>
                        )}
                        <td className="p-2 text-left">
                          <div className="w-full flex justify-between gap-4 items-center">
                            <p>
                              {(!isPbf && item.product_name) ||
                              (isPbf && item.product_names)
                                ? (isPbf
                                    ? item.product_names
                                    : item.product_name)[0]
                                : "-"}
                            </p>
                            {((!isPbf && item.product_name) ||
                              (isPbf && item.product_names)) &&
                            (isPbf ? item.product_names : item.product_name)
                              .length > 1 ? (
                              <p
                                className="text-blue-500 cursor-pointer whitespace-nowrap"
                                onMouseEnter={(e) => {
                                  setMoreProductArr(
                                    (isPbf
                                      ? item.product_names
                                      : item.product_name
                                    ).slice(1),
                                  );
                                  setAnchorElMoreProduct(e.currentTarget);
                                }}
                                onMouseLeave={() =>
                                  setAnchorElMoreProduct(null)
                                }
                              >
                                +
                                {(isPbf
                                  ? item.product_names
                                  : item.product_name
                                ).length - 1}{" "}
                                Lainnya
                              </p>
                            ) : null}
                          </div>
                        </td>
                        <td className="p-2 text-center">
                          {toIDR(isPbf ? item.total_bill : item.total_amount)}
                        </td>
                        {isPbf ? null : (
                          <td className="p-2 text-center">{item.source}</td>
                        )}
                        <td className="p-2 text-center">
                          <p
                            className={`w-full rounded-full py-1 px-3 whitespace-nowrap text-nowrap font-semibold ${
                              item.status === 1
                                ? "bg-[#F1EBE5] text-red-500"
                                : item.status === 2
                                  ? "bg-[#eee5f1] text-purple-500"
                                  : item.status === 4
                                    ? "bg-[#E5F1E6] text-green-500"
                                    : item.status === 5
                                      ? "bg-[#F1EBE5] text-yellow-500"
                                      : "bg-[#E8E8E8] text-black"
                            }`}
                          >
                            {
                              STATUS_ONLINE_BILLING.find(
                                (stat) => stat.id === item.status,
                              )?.value
                            }
                          </p>
                        </td>
                        <td className="p-2 text-center">
                          {moment(
                            isPbf ? item.date_bill_due : item.overdue_date,
                          ).format("DD/MM/YYYY")}
                        </td>
                        <td className="p-2 text-center">
                          <div className="w-full flex gap-2 items-center">
                            <a
                              className="bg-gray-100 border-gray-300 text-gray-500 border-2 rounded-md w-[100px] hover:bg-gray-300 p-1 disabled:border-none disabled:bg-gray-300 disabled:text-white"
                              href={`/online-billing${isPbf ? "-pbf" : ""}/detail/${isPbf ? item.billing_uid : item.invoice_number}`}
                            >
                              Detail
                            </a>
                            <button
                              className="w-[100px] bg-green-100 text-green-500 border-2 rounded-md hover:bg-green-300 p-1 disabled:bg-gray-300 disabled:text-white whitespace-nowrap disabled:bg-gray-300 disabled:text-white"
                              type="button"
                              disabled={item.status === 4 || item.status === 6}
                              onClick={() => {
                                setIsMultiple(false);
                                setSelectedBilling(item);
                                setOpenModalPay(true);
                              }}
                            >
                              {item.status === 1 || item.status === 3
                                ? "Bayar"
                                : "Cek Status"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        className="p-2 text-center border"
                        colSpan={isPbf ? 9 : 11}
                      >
                        {isLoading ? (
                          <CircularProgress size={20} />
                        ) : (
                          "Tidak ada data"
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {dataList && dataList.count > 0 && dataList.data !== null ? (
          <>
            <div className="mb-4">
              <PagePagination
                offset={reqBody.offset}
                setOffsetMemory={(limit, offset) => {
                  setBillingIdArr([]);
                  navigateTo(
                    offset,
                    limit,
                    reqBody.start_date,
                    reqBody.end_date,
                    reqBody.status,
                    reqBody.period,
                    reqBody.sort_date,
                    reqBody.sort_due_date,
                    reqBody.is_due_date,
                  );
                }}
                limit={reqBody.limit}
                setLimit={(e) => {
                  setBillingIdArr([]);
                  navigateTo(
                    1,
                    e,
                    reqBody.start_date,
                    reqBody.end_date,
                    reqBody.status,
                    reqBody.period,
                    reqBody.sort_date,
                    reqBody.sort_due_date,
                    reqBody.is_due_date,
                  );
                }}
                total={dataList.count}
              />
            </div>
          </>
        ) : null}

        <div
          className={`${
            billingIdArr.length === 0 ? "bg-white" : "bg-blue-500 text-white"
          }  fixed bottom-0 left-0 lg:left-[260px] min-h-[60px] px-[20px] py-[10px] w-full lg:w-[calc(100%-260px)] shadow-lg shadow-slate-900/20 shadow-b-2 shadow-r-[3px] -shadow-spread-2 text-[12px] flex items-center z-50`}
        >
          <div className="flex items-center justify-between items-center w-full">
            {isLoading ? (
              <CircularProgress size={20} />
            ) : dataList && !isLoading ? (
              <>
                {billingIdArr.length === 0 ? (
                  <div>
                    <p>{dataList.count > 0 ? dataList.date : ""}</p>
                    <div className="flex gap-4">
                      <p>
                        Total Transaksi :{" "}
                        <span className="text-[16px] font-bold">
                          {toIDR(
                            isPbf
                              ? dataList.total_bill
                              : dataList.total_payment,
                          )}
                        </span>
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between w-full items-center w-full">
                    <div className="w-full flex items-center gap-4">
                      <div className="text-white">
                        <p className="text-[14px]">Total</p>
                        <p className="text-[18px] font-bold">
                          {toIDR(getTotalBilling())}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={
                        billingIdArr.length < 1 ||
                        isAllPaid() ||
                        !isAllNotPaid()
                      }
                      onClick={() => {
                        setIsMultiple(true);
                        setOpenModalPay(true);
                      }}
                      className="rounded-xl text-[14px] disabled:bg-[#687B8E] text-white py-[10px] px-[42px] bg-[#FF8D40] hover:bg-orange-500"
                    >
                      {isAllPaid()
                        ? "Semua transaksi sudah lunas"
                        : isAllNotPaid()
                          ? "Bayar"
                          : "Semua transaksi harus belum dibayar"}
                    </button>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>

      <ModalPaymentStatus
        open={openModalPay}
        setOpen={setOpenModalPay}
        bill={isMultiple ? billingIdArr : [selectedBilling]}
        refetchList={() => getList({ ...reqBody, search: debounce })}
        isPbf={isPbf}
      />

      <Popover
        open={Boolean(anchorElStatus)}
        anchorEl={anchorElStatus}
        onClose={() => setAnchorElStatus(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <div className="w-full">
          <div
            className={`py-2 px-3 whitespace-nowrap w-full flex gap-2 items-center cursor-pointer hover:text-gray-300`}
            onClick={() => {
              navigateTo(
                1,
                reqBody.limit,
                reqBody.start_date,
                reqBody.end_date,
                reqBody.status.length < STATUS_ONLINE_BILLING.length
                  ? STATUS_ONLINE_BILLING.map((stat) => stat.id)
                  : [],
                reqBody.period,
                reqBody.sort_date,
                reqBody.sort_due_date,
                reqBody.is_due_date,
              );
              setReqBody((prevVal) => ({
                ...prevVal,
                status:
                  reqBody.status.length < STATUS_ONLINE_BILLING.length
                    ? STATUS_ONLINE_BILLING.map((stat) => stat.id)
                    : [],
              }));
            }}
          >
            <input
              type="checkbox"
              checked={
                reqBody.status.length === STATUS_ONLINE_BILLING.length &&
                reqBody.status.length > 0
              }
              className="cursor-pointer w-[16px] h-[16px]"
            />
            Semua
          </div>
          {STATUS_ONLINE_BILLING.map((item, i) => (
            <div
              className={`py-2 px-3 whitespace-nowrap w-full border-t flex gap-2 items-center cursor-pointer hover:text-gray-300`}
              key={i}
              onClick={() => {
                const index = reqBody.status.findIndex(
                  (stat) => stat === item.id,
                );
                setBillingIdArr([]);
                const newStatus =
                  index < 0
                    ? [...reqBody.status, item.id]
                    : [
                        ...reqBody.status.slice(0, index),
                        ...reqBody.status.slice(index + 1),
                      ];
                navigateTo(
                  1,
                  reqBody.limit,
                  reqBody.start_date,
                  reqBody.end_date,
                  newStatus,
                  reqBody.period,
                  reqBody.sort_date,
                  reqBody.sort_due_date,
                  reqBody.is_due_date,
                );
                setReqBody((prevVal) => ({
                  ...prevVal,
                  status: newStatus,
                }));
              }}
            >
              <input
                type="checkbox"
                className="cursor-pointer w-[16px] h-[16px]"
                checked={reqBody.status.includes(item.id)}
              />
              {item.value}
            </div>
          ))}
        </div>
      </Popover>

      <Dialog
        open={openCalendar}
        onClose={() => {
          setOpenCalendar(false);
        }}
      >
        <DialogTitle>Pilih Tanggal</DialogTitle>
        <DialogContent>
          <div className="flex">
            <DatePicker
              selected={startDate}
              onChange={(dates) => {
                const [start, end] = dates;
                setStartDate(start);
                setEndDate(end);
              }}
              startDate={startDate}
              endDate={endDate}
              selectsRange
              inline
            />
          </div>
          <button
            className="bg-indigo-500 rounded text-white px-2 py-1 font-semibold w-full"
            onClick={() => {
              setOpenCalendar(false);
              setPeriodDummy(10);
              setReqBody((prevVal) => ({
                ...prevVal,
                start_date: startDate
                  ? moment(startDate).format("YYYY-MM-DD")
                  : "",
                end_date: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
              }));
              navigateTo(
                reqBody.offset,
                reqBody.limit,
                startDate ? moment(startDate).format("YYYY-MM-DD") : "",
                endDate ? moment(endDate).format("YYYY-MM-DD") : "",
                reqBody.status,
                reqBody.period,
                reqBody.sort_date,
                reqBody.sort_due_date,
                reqBody.is_due_date,
              );
            }}
          >
            Konfirmasi
          </button>
        </DialogContent>
      </Dialog>

      <Popper
        placement="bottom"
        open={Boolean(anchorElMoreProduct)}
        anchorEl={anchorElMoreProduct}
        sx={{ maxWidth: "200px" }}
        modifiers={[
          {
            name: "arrow",
            enabled: true,
            // options: {
            //   element: arrowRef,
            // },
          },
        ]}
      >
        <div className="bg-white text-[10px] p-2 rounded-md border text-wrap whitespace-wrap overflow-hidden break-words">
          {moreProductArr.map((item, i) => `${i + 1}. ${item}`).join(", ")}
        </div>
      </Popper>
    </>
  );
};

export default OnlineBilling;
